const _ = require("lodash");
const Font = require("../../util/Font");
const ImageCache = require("../../util/ImageCache");

// Returns the measured width including xPadding.
const computeNodeFullWidth = function(measure) {
	return measure.width + 2 * (measure.xPadding || 0);
};

const scaleFont = function(fontInfo, scaleAmount) {
	return _.extend({}, fontInfo, {size: fontInfo.size * scaleAmount});
};

const computeFontSizeToFitRect = function(params) {
	const {digitWidth, rect, digitFit, paddingArray, fontSize} = params;

	const fontRatioToFit =
		rect.w /
		(digitFit * digitWidth + (_.min(_.flattenDeep([paddingArray])) || 0));
	return fontRatioToFit * fontSize;
};

// Changes the font to the one used for variables.
const toVariableFont = function(fontInfo) {
	if (fontInfo.family[0] !== "lizzie") {
		return {
			italic: true,
			bold: fontInfo.bold,
			size: fontInfo.size * 1,
			family: ["Lora", "serif"],
		};
	}
	return fontInfo;
};

// Returns xBuffer and vertical measuring only.
const getNaturalBlankMeasure = function(rc, fontInfo) {
	const lineWidth = getLineWidth(rc, fontInfo);
	const xBuffer = getSpaceWidth(rc, fontInfo) / 3;
	const yBuffer = 2 * lineWidth;
	return {
		top: fontInfo.size / 2 + yBuffer,
		bottom: fontInfo.size / 2 + yBuffer,
		xBuffer,
		yBuffer,
	};
};

// We sometimes need to add additional space to the end of boxed, bigboxed,
// and fraction nodes to vertically center characters that extend downward like
// y, p, or (.
const calculateAdditionalBottomSpace = function(node, lineWidth) {
	const textChars = getTextCharactersFromChildren(node, {chars: ""});
	if (/[gjpqy\(\)\[\]\@]/.test(textChars.chars)) {
		return (7 / 2) * lineWidth;
	}
	return 0;
};

// textChars is an object with string property chars.
// This function modifies the chars property in textChars
// so that it is a string containing every character used in
// the node's text children.
const getTextCharactersFromChildren = function(node, textChars) {
	if (node.type === "text") {
		textChars.chars += node.text;
	} else {
		_.each(node.children, function(childNode) {
			if (
				childNode.type !== "fraction" &&
				childNode.type !== "boxed" &&
				childNode.type !== "bigboxed"
			) {
				getTextCharactersFromChildren(childNode, textChars);
			}
		});
	}
	return textChars;
};

//Draws a long division sign/bracket with top left corner (x,y) and specified width and height
const drawLongDivisionBracket = function(context, x, y, width, height) {
	const radius = height / Math.sqrt(2);
	context.beginPath();
	const circleX = x - (Math.sqrt(2) / 2) * radius;
	context.arc(
		circleX,
		y + height / 2,
		radius,
		2 * Math.PI - Math.PI / 4,
		Math.PI / 4
	);
	context.moveTo(x + 3, y + 3);
	context.lineTo(x, y);
	context.lineTo(x + width, y);
	context.stroke();
};

const parseCommaNumber = function(numString) {
	if (typeof numString !== "string") {
		return numString;
	}
	numString = numString.replace(/[,\s]/g, "");
	return parseFloat(numString);
};

const withCommas = function(num) {
	//code from http://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
	const parts = num.toString().split(".");
	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	return parts.join(".");
};

let textWidthCache;
const resetTextWidthCache = function() {
	textWidthCache = {
		"|": new Map(),
		"0": new Map(),
		" ": new Map(),
	};
};
resetTextWidthCache();

const getTextWidthWithCache = function(rc, fontInfo, text) {
	const context = rc.context;
	const contextFont = fontInfo ? Font.fromInfo(fontInfo) : context.font;
	const cache = textWidthCache[text];
	let result = cache.get(contextFont);
	if (!result) {
		context.save();
		context.font = contextFont;
		result = context.measureText(text).width;
		context.restore();
		cache.set(contextFont, result);
	}
	return result;
};

const roundValForLineWidth = function(val, lineWidth) {
	if (lineWidth % 2 === 0) {
		return Math.round(val);
	} else {
		return Math.round(val - 0.5) + 0.5;
	}
};

const getIntegerLineWidth = function(rc, fontInfo) {
	return Math.round(getLineWidth(rc, fontInfo));
};

const getLineWidth = function(rc, fontInfo) {
	return getTextWidthWithCache(rc, fontInfo, "|") / 4;
};

const getDigitWidth = function(rc, fontInfo) {
	return getTextWidthWithCache(rc, fontInfo, "0");
};

const getSpaceWidth = function(rc, fontInfo) {
	return getTextWidthWithCache(rc, fontInfo, " ");
};

const getLinePadding = function(fontInfo) {
	return fontInfo.size / 12;
};

const critterInfo = {
	fl: {img: "flamingoat.svg", ratio: 311.8 / 328.7},
	oc: {img: "octopug.svg", ratio: 209.8 / 191.4},
	or: {img: "orangutoad.svg", ratio: 211.6 / 228},
	po: {img: "porcupuma.svg", ratio: 259.3 / 267.5},
	sp: {img: "spotted-jackalope.svg", ratio: 191.1 / 143.3},
	ta: {img: "tarantulemur.svg", ratio: 202.2 / 180.8},
	to: {img: "tortusk.svg", ratio: 246 / 185.3},
	al: {img: "alligopher.svg", ratio: 245.4 / 275.3},
	bu: {img: "buffalobster.svg", ratio: 275.8 / 321.9},
	cp: {img: "chimpanda.svg", ratio: 262.5 / 231.7},
	cb: {img: "chimbanda.svg", ratio: 262.5 / 231.7},
	cm: {img: "chipmonkey.svg", ratio: 191.2 / 219.7},
	hi: {img: "hippopotamoose.svg", ratio: 262 / 359.5},
	pe: {img: "pelicamel.svg", ratio: 329 / 324.8},
	rh: {img: "rhinocerabbit.svg", ratio: 255.6 / 254.9},
};

const pirateNumberInfo = {
	// char mapped to: {mappedKeys, img, btenImg}
	c: {mappedKeys: ["c"], img: "c.svg", btenImg: "square.svg"},
	x: {mappedKeys: ["x"], img: "x.svg", btenImg: "line.svg"},
	o: {mappedKeys: ["o", "."], img: "dot.svg", btenImg: "circle.svg"},
};

const getPirateKeyboardImg = function(keyboardType, mapInfo) {
	return keyboardType === "btenPirate" ? mapInfo.btenImg : mapInfo.img;
};

// Any single letter symbol names should have the same mappedKey for intuitive problem entry
const symbolInfo = {
	apple: {mappedKey: null, img: "apple.svg", name: "apple"},
	arrow: {mappedKey: null, img: "arrow.svg", name: "arrow"},
	balloon: {mappedKey: "u", img: "balloon.svg", name: "balloon"},
	bee: {mappedKey: null, img: "bee.svg", name: "bee"},
	book: {mappedKey: null, img: "book.svg", name: "book"},
	bsign: {mappedKey: null, img: "b-sign.svg", name: "bsign"},
	bug: {mappedKey: null, img: "bug.svg", name: "bug"},
	burst: {mappedKey: "y", img: "burst.svg", name: "burst"},
	cat: {mappedKey: null, img: "cat.svg", name: "cat"},
	ci: {mappedKey: "c", img: "circle.svg", name: "circle"},
	cloud: {mappedKey: null, img: "cloud.svg", name: "cloud"},
	diamond: {mappedKey: null, img: "diamond.svg", name: "diamond"},
	dm: {mappedKey: "e", img: "diamond-regular.svg", name: "diamond"},
	donut: {mappedKey: "d", img: "circle-frame.svg", name: "circle"},
	envelope: {mappedKey: null, img: "envelope.svg", name: "envelope"},
	fish: {mappedKey: "z", img: "fish.svg", name: "fish"},
	fh: {mappedKey: null, img: "fish-regular.svg", name: "fish"},
	flower: {mappedKey: "f", img: "flower.svg", name: "flower"},
	glove: {mappedKey: null, img: "glove.svg", name: "glove"},
	h: {mappedKey: "h", img: "h.svg", name: "H"},
	heart: {mappedKey: null, img: "heart.svg", name: "heart"},
	ht: {mappedKey: "g", img: "heart-regular.svg", name: "heart"},
	hexagon: {mappedKey: "q", img: "hexagon.svg", name: "hexagon"},
	house: {mappedKey: null, img: "house.svg", name: "house"},
	hs: {mappedKey: null, img: "house-regular.svg", name: "house"},
	j: {mappedKey: "j", img: "j.svg", name: "J"},
	key: {mappedKey: null, img: "key.svg", name: "key"},
	l: {mappedKey: "l", img: "l.svg", name: "L"},
	leaf: {mappedKey: "w", img: "leaf.svg", name: "leaf"},
	lightning: {mappedKey: "k", img: "lightning.svg", name: "lightning"},
	lock: {mappedKey: null, img: "lock.svg", name: "lock"},
	magnifier: {mappedKey: null, img: "magnifier.svg", name: "magnifier"},
	moon: {mappedKey: "i", img: "moon.svg", name: "moon"},
	octagon: {mappedKey: "r", img: "octagon.svg", name: "octagon"},
	pencil: {mappedKey: null, img: "pencil.svg", name: "pencil"},
	pentagon: {mappedKey: "p", img: "pentagon.svg", name: "pentagon"},
	planet: {mappedKey: null, img: "planet.svg", name: "planet"},
	projector: {mappedKey: null, img: "projector.svg", name: "projector"},
	puzzle: {mappedKey: null, img: "puzzle.svg", name: "puzzle"},
	s: {mappedKey: "s", img: "s.svg", name: "s"},
	square: {mappedKey: null, img: "square.svg", name: "square"},
	sq: {mappedKey: "b", img: "square-regular.svg", name: "square"},
	star4: {mappedKey: "m", img: "star-4-points.svg", name: "star"},
	star5: {mappedKey: "n", img: "star-5-points.svg", name: "star"},
	star8: {mappedKey: "o", img: "star-8-points.svg", name: "star"},
	ribbon: {mappedKey: null, img: "ribbon.svg", name: "ribbon"},
	t: {mappedKey: "t", img: "t.svg", name: "T"},
	trash: {mappedKey: null, img: "trash.svg", name: "trash"},
	tree: {mappedKey: null, img: "tree.svg", name: "tree"},
	triangle: {mappedKey: null, img: "triangle.svg", name: "triangle"},
	tr: {mappedKey: "a", img: "triangle-regular.svg", name: "triangle"},
	trophy: {mappedKey: null, img: "trophy.svg", name: "trophy"},
	v: {mappedKey: "v", img: "v.svg", name: "V"},
	x: {mappedKey: "x", img: "x.svg", name: "X"},
};

const symbolColors = [
	"#000000",
	"#15B01A",
	"#233E9B",
	"#252525",
	"#323333",
	"#424154",
	"#43712D",
	"#444444",
	"#4A495E",
	"#52A0CC",
	"#548E39",
	"#5A5B5B",
	"#5C67E6",
	"#66C9A2",
	"#75250F",
	"#777777",
	"#7AAFCC",
	"#825010",
	"#932F13",
	"#983E30",
	"#999999",
	"#9E5CE6",
	"#A36415",
	"#A3D96D",
	"#B2AA8D",
	"#B68AE6",
	"#BBBBBB",
	"#BDDE9B",
	"#BF4E3D",
	"#C6BD9D",
	"#D99343",
	"#DDDDDD",
	"#E50000",
	"#E65CAE",
	"#E68AC1",
	"#F2A44B",
	"#FF6673",
	"#FFB366",
	"#FFCC99",
	"#FFF68C",
	"#E10000",
	"#09009C",
	"#E15858",
	"#009122",
	"#FF7700",
	"#8100A6",
	"#DB398F",
	"#E15858",
	"#0087B8",
	"#00A669",
	"#FFC800",
];

const getSymbolBatex = function(batexToConvert, color) {
	let newBatex = "";
	const lettersToReplace = _.map(symbolInfo, "mappedKey");

	for (let i = 0; i < batexToConvert.length; i++) {
		const char = batexToConvert.substring(i, i + 1);
		if (_.includes(lettersToReplace, char)) {
			const symbolName = _.findKey(symbolInfo, x => x.mappedKey === char);
			const colorArg = color === undefined ? "" : "[" + color + "]";

			newBatex += "\\symbol" + colorArg + "{" + symbolName + "}";
		} else {
			newBatex += char;
		}
	}

	return newBatex;
};

const getPirateNumberBatex = function(batexToConvert, color, pirateNumberType) {
	const convertedStr = _.toLower(batexToConvert.replace(/\./g, "o"));
	if (!/^[cxo]*$/.test(convertedStr)) return "";
	const colorArg = color ? "[" + color + "]" : "";
	if (pirateNumberType === "btenPirate") {
		return `$\\unsortedBtenPirateNumber${colorArg}{${convertedStr}}$`;
	} else {
		return `$\\unsortedPirateNumber${colorArg}{${convertedStr}}$`;
	}
};

const basicDrawNodeImage = function(rc, x, y, node, measure) {
	const context = rc.context;
	const {width, top, bottom, imgSrc} = measure;
	const img = ImageCache.src(imgSrc, true);

	context.drawImage(img, x, y - top, width, top + bottom);
};

const drawCurlyBrace = function(rc, size, x, y, direction = "r") {
	const context = rc.context;
	const lineWidth = context.lineWidth;
	context.beginPath();
	context.moveTo(x, y);
	const radius = 3 * lineWidth;
	if (direction === "r") {
		//right curly
		context.arcTo(x + radius, y, x + radius, y + radius, radius);
		context.lineTo(x + radius, y + size / 2 - radius);
		context.arcTo(
			x + radius,
			y + size / 2,
			x + 2 * radius,
			y + size / 2,
			radius
		);
		context.arcTo(
			x + radius,
			y + size / 2,
			x + radius,
			y + size / 2 + radius,
			radius
		);
		context.lineTo(x + radius, y + size - radius);
		context.arcTo(x + radius, y + size, x, y + size, radius);
		context.stroke();
	} else if (direction === "l") {
		//left curly
		context.arcTo(x - radius, y, x - radius, y + radius, radius);
		context.lineTo(x - radius, y + size / 2 - radius);
		context.arcTo(
			x - radius,
			y + size / 2,
			x - 2 * radius,
			y + size / 2,
			radius
		);
		context.arcTo(
			x - radius,
			y + size / 2,
			x - radius,
			y + size / 2 + radius,
			radius
		);
		context.lineTo(x - radius, y + size - radius);
		context.arcTo(x - radius, y + size, x, y + size, radius);
		context.stroke();
	} else if (direction === "t") {
		//top curly
		context.arcTo(x, y - radius, x + radius, y - radius, radius);
		context.lineTo(x + size / 2 - radius, y - radius);
		context.arcTo(
			x + size / 2,
			y - radius,
			x + size / 2,
			y - 2 * radius,
			radius
		);
		context.arcTo(
			x + size / 2,
			y - radius,
			x + size / 2 + radius,
			y - radius,
			radius
		);
		context.lineTo(x + size - radius, y - radius);
		context.arcTo(x + size + radius, y - radius, x + size + radius, y, radius);
		context.stroke();
	} else if (direction === "b") {
		//bottom curly
		context.arcTo(x, y + radius, x + radius, y + radius, radius);
		context.lineTo(x + size / 2 - radius, y + radius);
		context.arcTo(
			x + size / 2,
			y + radius,
			x + size / 2,
			y + 2 * radius,
			radius
		);
		context.arcTo(
			x + size / 2,
			y + radius,
			x + size / 2 + radius,
			y + radius,
			radius
		);
		context.lineTo(x + size - radius, y + radius);
		context.arcTo(x + size + radius, y + radius, x + size + radius, y, radius);
		context.stroke();
	}

	return radius;
};

module.exports = {
	computeNodeFullWidth,
	scaleFont,
	computeFontSizeToFitRect,
	toVariableFont,
	getNaturalBlankMeasure,
	calculateAdditionalBottomSpace,
	drawCurlyBrace,
	drawLongDivisionBracket,
	parseCommaNumber,
	withCommas,
	roundValForLineWidth,
	getIntegerLineWidth,
	resetTextWidthCache,
	getLineWidth,
	getDigitWidth,
	getSpaceWidth,
	getLinePadding,
	critterInfo,
	symbolInfo,
	pirateNumberInfo,
	getPirateKeyboardImg,
	symbolColors,
	getPirateNumberBatex,
	getSymbolBatex,
	basicDrawNodeImage,
};
